import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { Alert, Button, LoadingIndicator } from 'components/common';
import { TwoFactorSetup, TwoFactorSuccess } from 'components/common/auth';
import { Form } from 'components/common/form';

import type { FormApi } from 'informed';

const LoginTwoFactorSetup = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { next },
    user
  } = useReduxState();

  const formApi = useRef<FormApi>(null);

  const [activating, setActivating] = useState(false);
  const [continuing, setContinuing] = useState(false);

  const { data, error, loading, updateData } = useApiData({
    url: '/auth/totp/status'
  });

  const handleContinue = () => {
    setContinuing(true);
    window.location.href = next || '/';
  };

  const verify = async (values) => {
    try {
      setActivating(true);

      const response = await api.post('/auth/totp/activate', {
        code: values.verificationCode,
        secret: data?.newSecret
      });
      if (response?.data?.success) {
        await updateData();
      } else if (response?.data?.error === 'invalid_code') {
        formApi.current.setError(
          'verificationCode',
          messages.authTwoFactorInvalidCode
        );
      } else {
        throw new Error('error');
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setActivating(false);
    }

    return false;
  };

  return loading ? (
    <LoadingIndicator />
  ) : error ? (
    <Alert text={messages.commonError} type="error-light" />
  ) : (
    <Form getApi={(api) => (formApi.current = api)} onSubmit={verify}>
      {() => (
        <div className="content">
          {data?.isActive ? (
            <div className="success">
              <TwoFactorSuccess />

              <div className="footer">
                <Button
                  category="success"
                  loading={continuing}
                  onClick={handleContinue}
                  text={messages.commonContinue}
                />
              </div>
            </div>
          ) : (
            <div className="setup">
              <TwoFactorSetup
                isRequired={true}
                secret={data?.newSecret}
                userEmail={user?.email || user?.username}
              />

              <div className="footer">
                <Button
                  category="success"
                  loading={activating}
                  text={messages.authTwoFactorVerifyAndActivate}
                  type="submit"
                />
              </div>
            </div>
          )}

          <style jsx>{`
            .footer {
              margin-top: 15px;
              text-align: right;
            }
          `}</style>
        </div>
      )}
    </Form>
  );
};

export default LoginTwoFactorSetup;
